import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SearchIcon } from "@heroicons/react/solid";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";

import Loading from "../../../components/Loading";
import {
  createDiscount,
  getDiscountByDestination,
} from "../../../redux2/discount/discountSlice";

const DiscountModal = ({ show, setOpen, companies, isLoading }) => {
  const { discount, isLoading: discountLoading } = useSelector(
    (state) => state.discount
  );

  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [form, setForm] = useState({
    serviceCharge: discount?.serviceCharge || "",
    destination: "",
  });

  const dispatch = useDispatch();

  useEffect(() => {
    if (form.destination) {
      dispatch(getDiscountByDestination(form.destination));
    }
  }, [dispatch, form.destination]);

  useEffect(() => {
    if (companies && !isLoading) {
      setFilteredCompanies(companies);
    }
  }, [companies, isLoading]);

  useEffect(() => {
    if (discount) {
      setForm({
        serviceCharge: discount?.serviceCharge || "",
        destination: discount?.destination || "",
      });
      setSelectedCompanies((prevSelected) => [
        ...new Set([...prevSelected, ...(discount?.companyIds || [])]),
      ]);
    } else {
      setForm({
        serviceCharge: "",
      });
      setSelectedCompanies([]);
    }
  }, [discount]);

  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    setSelectedCompanies(isChecked ? companies?.map((c) => c._id) : []);
  };

  const handleCompanySelect = (e, company) => {
    const isChecked = e.target.checked;
    setSelectedCompanies((prevSelected) =>
      isChecked
        ? [...prevSelected, company?._id]
        : prevSelected.filter((c) => c !== company?._id)
    );
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    const filtered = companies.filter((company) =>
      company.name.toLowerCase().includes(value)
    );
    setFilteredCompanies(filtered);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prevForm) => ({
      ...prevForm,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.serviceCharge === "" || form.destination === "") {
      toast.error("Please fill all fields");
      return;
    }

    const data = {
      companyIds: selectedCompanies,
      serviceCharge: +form.serviceCharge,
      destination: form.destination,
    };

    dispatch(createDiscount(data));
    setForm({
      serviceCharge: "",
      destination: "",
    });
    setSelectedCompanies([]);
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
    setForm({
      serviceCharge: "",
      destination: "",
    });
    setSelectedCompanies([]);
  };

  if (isLoading) return <Loading />;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      className="modal-right"
    >
      <Modal.Body>
        <div className="p-6 mx-auto w-full max-w-3xl bg-white rounded-lg">
          <h5 className="mb-6 text-3xl font-semibold text-center text-gray-800">
            Select Agencies for Discount
          </h5>
          <form onSubmit={handleSubmit}>
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center w-full">
                <input
                  type="checkbox"
                  id="select-all"
                  className="w-5 h-5 text-indigo-600 transition duration-200 ease-in-out form-checkbox focus:ring-indigo-500"
                  onChange={handleSelectAll}
                />
                <label
                  htmlFor="select-all"
                  className="mt-2 ml-3 text-lg font-medium text-gray-700 cursor-pointer"
                >
                  Select All
                </label>
              </div>

              <div className="relative w-full max-w-sm text-end">
                <input
                  type="text"
                  placeholder="Search agencies..."
                  className="py-2 pr-4 pl-10 w-full text-sm text-gray-700 rounded-lg border border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  onChange={handleSearch}
                />
                <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                  <SearchIcon className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {filteredCompanies.map((company, index) => (
                <div
                  className="flex items-center p-4 bg-gray-50 rounded-lg shadow-sm transition duration-200 hover:bg-gray-100"
                  key={company._id}
                >
                  <span className="mr-4 text-sm font-medium text-gray-600">
                    {index + 1}.
                  </span>
                  <label
                    htmlFor={`company-list-${company._id}`}
                    className="flex justify-between w-full cursor-pointer"
                  >
                    <span className="text-base font-medium text-gray-800">
                      {company.name}
                    </span>
                    <input
                      type="checkbox"
                      id={`company-list-${company._id}`}
                      className="w-5 h-5 text-indigo-600 transition duration-200 form-checkbox"
                      checked={selectedCompanies.some((c) => c === company._id)}
                      onChange={(e) => handleCompanySelect(e, company)}
                    />
                  </label>
                </div>
              ))}
            </div>
            <div className="flex flex-row gap-4 mt-4">
              <select
                className="block p-2 w-1/2 text-lg text-black bg-white rounded-lg border border-black focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                name="destination"
                id="destination"
                placeholder="Destination"
                onWheel={(e) => e.target.blur()}
                onChange={handleChange}
                value={form.destination}
                required
              >
                <option value="">Select Destination</option>
                <option value="DXB">DXB</option>
                <option value="ADD">ADD</option>
                <option value="JED">JED</option>
                <option value="BAH">BAH</option>
                <option value="RUH">RUH</option>
                <option value="BEY">BEY</option>
              </select>
              <input
                type="number"
                id="serviceCharge"
                className="block p-2 w-1/2 text-lg text-black bg-white rounded-lg border border-black focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                onWheel={(e) => e.target.blur()}
                name="serviceCharge"
                placeholder="Discount Service Charge"
                onChange={handleChange}
                value={form.serviceCharge}
                required
              />
            </div>
          </form>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex gap-4 justify-end w-full">
          <button
            type="button"
            className="px-5 py-2 text-gray-700 bg-gray-200 rounded-md transition duration-200 hover:bg-gray-300"
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-5 py-2 text-white bg-indigo-600 rounded-md transition duration-200 hover:bg-indigo-700"
            onClick={handleSubmit}
            disabled={discountLoading}
          >
            Submit
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default DiscountModal;
